import { ProviderService } from 'src/app/core/service/provider.service';
import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewChecked } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { HelperService } from 'src/app/core/service/helper.service';
import { ProviderListService } from 'src/app/core/service/grid-data/grid-data-provider.service';
import copyToClipboard from 'copy-to-clipboard';
import { SettingService } from 'src/app/core/service/setting.service';
import { MemberService } from 'src/app/core/service/member.service';
import { AppConfiguration, AppConfigService } from 'src/app/core/service/app-config.service';
import { TranslateService } from '@ngx-translate/core';
import { Dictionary } from 'src/app/core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { sortBy } from 'lodash';

@Component({
  standalone: false,
  selector: 'app-provider-search',
  templateUrl: './provider-search.component.html',
  styleUrls: ['./provider-search.component.scss'],
  providers: [
    ProviderListService   // Create a new instance of the Provider List to not affect the provider admin setup
  ]
})
export class ProviderSearchComponent implements OnInit, AfterViewChecked
{
  @ViewChild('splitter', { read: ElementRef }) public splitter: ElementRef;

  @Input() public allowSelection = false;
  @Input() public singleSelectionOnly = false;
  @Input() public memberId: number;
  @Input() public allowFacilitySearch = true;

  @Output() public selectProvider = new EventEmitter();

  public pageSizes = [5, 10, 25, 50, 100];
  public searchForm: any;
  public total: number;
  public gridView: GridDataResult;
  public genders = [];
  public states = [];
  public specialties = [];
  public showMapTitle: string;
  public patientLabel = {};
  public allLanguages = [];
  public languagesFiltered = [];
  public keys: number[] = [];
  public loaded = false;

  /**
   * All specialty codes
   */
  public allSpecialties = [];

  /**
   * Filtered list of specialties for drop-down.
   */
  public specialtiesFiltered: Dictionary[] = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Maximum number of items for specialty virtualization. This is to accelerate the DOM performance.
   */
  public virtualLookups: any = {
    itemHeight: 30
  };

  /**
   * Provider list Kendo grid state.
   */
  public gridState: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  private providerDistanceDisplay: string;
  private appConfig: AppConfiguration;

  constructor(
    private fb: UntypedFormBuilder,
    private helperService: HelperService,
    private providerListService: ProviderListService,
    private settingService: SettingService,
    private providerService: ProviderService,
    private memberService: MemberService,
    private appConfigService: AppConfigService,
    private translateService: TranslateService,
    private messageHandlerService: MessageHandlerService,
    private dictionaryService: DictionaryService
  ) { }

  public get showMap(): boolean
  {
    return (this.appConfig && this.appConfig.google && this.appConfig.google.mapBaseUrl !== null);
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.providerListService.loading;
  }

  public get firstName(): UntypedFormControl
  {
    return this.searchForm.get('firstName');
  }

  public get lastName(): UntypedFormControl
  {
    return this.searchForm.get('lastName');
  }

  public get middleName(): UntypedFormControl
  {
    return this.searchForm.get('middleName');
  }

  public get organization(): UntypedFormControl
  {
    return this.searchForm.get('organization');
  }

  public get facility(): UntypedFormControl
  {
    return this.searchForm.get('facility');
  }

  public get streetLine(): UntypedFormControl
  {
    return this.searchForm.get('streetLine');
  }

  public get gender(): UntypedFormControl
  {
    return this.searchForm.get('gender');
  }

  public get npi(): UntypedFormControl
  {
    return this.searchForm.get('npi');
  }

  public get specialtyName(): UntypedFormControl
  {
    if (this.searchForm !== undefined)
    {
      return this.searchForm.get('specialtyName');
    }
  }

  public get languages(): UntypedFormControl
  {
    if (this.searchForm !== undefined)
    {
      return this.searchForm.get('languages');
    }
  }

  public get city(): UntypedFormControl
  {
    if (this.searchForm !== undefined)
    {
      return this.searchForm.get('city');
    }
  }

  public get state(): UntypedFormControl
  {
    if (this.searchForm !== undefined)
    {
      return this.searchForm.get('state');
    }
  }

  public get postalCode(): UntypedFormControl
  {
    if (this.searchForm !== undefined)
    {
      return this.searchForm.get('postalCode');
    }
  }

  public ngOnInit(): void
  {
    this.buildForm();
    this.resetSearch();

    this.appConfig = this.appConfigService.getConfig();
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.providerDistanceDisplay = sessionSetting.providerDistanceDisplay;
        this.patientLabel = { patientLabel: sessionSetting.patientLabel.toLowerCase() };
        this.translateService.get(['SHOW-MAP-TITLE']).subscribe({
          next: (res) =>
          {
            this.showMapTitle = res['SHOW-MAP-TITLE'].replace('{{patientLabel}}', sessionSetting.patientLabel);
          }
        });
      }
    });

    // Get specialties
    this.dictionaryService.getCachedSpecialties().subscribe({
      next: (data) =>
      {
        this.allSpecialties = data;
        this.filterSpecialties('');
      }
    });
    this.loadLanguages();
  }

  public ngAfterViewChecked(): void
  {
    // update splitter bar once we are sure the page is loaded
    this.updateSplitterBar();
  }

  /**
   * Check if search button is disabled.
   *
   * @returns boolean Return true is the search is disabled due to lack of search criteria.
   */
  public searchDisabled(): boolean
  {
    if (this.searchForm !== undefined)
    {
      return ((!this.firstName.value || this.firstName.value.replace(/\s/g, '') === '') &&
      (!this.lastName.value || this.lastName.value.replace(/\s/g, '') === '') &&
      (!this.middleName.value || this.middleName.value.replace(/\s/g, '') === '') &&
      (!this.organization.value || this.organization.value.replace(/\s/g, '') === '') &&
      (!this.facility.value || this.facility.value.replace(/\s/g, '') === '') &&
      (!this.streetLine.value || this.streetLine.value.replace(/\s/g, '') === '') &&
      (!this.city.value || this.city.value.replace(/\s/g, '') === '') &&
      (!this.state.value || this.state.value === undefined) &&
      (!this.gender.value || this.gender.value === undefined) &&
      (!this.postalCode.value || this.postalCode.value.replace(/\s/g, '') === '') &&
      (!this.npi.value || this.npi.value.replace(/\s/g, '') === '') &&
      (!this.languages.value || this.languages.value === undefined || this.languages.value.length === 0) && (!this.specialtyName.value || this.specialtyName.value === undefined));
    }
    else
    {
      return true;
    }
  }

  /**
   * Search Providers based on criteria
   *
   * @param resetPage Wehn true, the page offset is reset to 0.
   */
  public searchProvider(resetPage = false): void
  {
    // Must always reset page offset to 0. Except if changing page.
    if (resetPage)
    {
      this.gridState.skip = 0;
    }

    this.gridState = {
      filter: {
        filters: [], logic: 'and'
      },
      group: [],
      skip: this.gridState.skip !== undefined ? this.gridState.skip : 0,
      sort: this.gridState.sort !== undefined ? this.gridState.sort : [{
        field: this.memberId !== undefined && this.memberId > 0 ? 'distance' : 'firstName',
        dir: 'asc'
      }],
      take: this.gridState.take !== undefined ? this.gridState.take : this.appConfig.pageSize
    };

    // Set search criteria
    this.setSearchFilters();

    // For Provider search in Appointment context, we send the memberId in context to calculate the
    // distance between the patient and provider.
    let additionalQueryString = '';
    if (this.memberId !== undefined && this.memberId > 0)
    {
      additionalQueryString = `&memberId=${this.memberId}`;
    }

    if (this.allowFacilitySearch)
    {
      additionalQueryString += '&facilityView=true';
    }

    // Query Providers
    this.resetSearch();
    this.providerListService.queryByState(this.gridState, (providers: any) =>
    {
      // Set a rowId for selection composed with provider and facility id
      providers.data.forEach(
        data =>
        {
          // Make a composite rowId
          data.rowId = data.id + (data.facilityId !== null ? `|${data.facilityId}` : '');
          data.languagesName = data.languages !== null ? data.languages.map(x => x.displayName).join(', ') : '';
        });
      this.total = providers.total;
      this.gridView = {
        data: providers.data,
        total: providers.total
      };
    }, additionalQueryString);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.gridState = state;
    this.searchProvider();
  }

  /**
   * Clear all the Provider search criteria.
   */
  public clearSearch(): void
  {
    this.firstName.setValue(null);
    this.lastName.setValue(null);
    this.middleName.setValue(null);
    this.organization.setValue(null);
    this.facility.setValue(null);
    this.npi.setValue(null);
    this.specialtyName.setValue(null);
    this.languages.setValue(null);
    this.streetLine.setValue(null);
    this.city.setValue(null);
    this.state.setValue(null);
    this.postalCode.setValue(null);
    this.gender.setValue(null);
    this.clearGrid();
    this.resetSearch();
  }

  /**
   * Method called when the select button have been hit.
   * The method construct a selected array that will contains the list of selected providers.
   */
  public onSelectedProviders(): void
  {
    const selected = [];
    this.keys.forEach(x =>
    {
      const recordIndex = this.gridView.data.findIndex(provider => provider.rowId === x);
      if (recordIndex >= 0)
      {
        this.gridView.data[recordIndex].directorySource = 'CHANNELS';
        selected.push(this.gridView.data[recordIndex]);
      }
    });
    this.selectProvider.emit(selected);
  }

  /**
   * Provide the full provider name to display.
   *
   * @param dataItem Current row dataItem.
   */
  public getProviderName(dataItem: any): string
  {
    return (dataItem !== undefined && dataItem !== null) ?
      (dataItem.firstName !== '' && dataItem.lastName !== '' ? `${dataItem.firstName} ${dataItem.lastName}` : `${dataItem.organization}`)
      : '';
  }

  /**
   * Copy the direct address to clipboard.
   *
   * @param directAddress Direct address to copy.
   */
  public copyDirectAddress(directAddress: string): void
  {
    copyToClipboard(directAddress);
  }

  public getDistanceDisplayText(): string
  {
    return this.providerService.getDistanceDisplayText(this.providerDistanceDisplay);
  }

  public openMap(dataItem: any): void
  {
    // get Member by ID from REST service
    this.memberService.getMember(this.memberId).subscribe({
      next: (member) =>
      {
        if (member && member.address && member.address.longitude && member.address.latitude)
        {
          const url = `${this.appConfig.google.mapBaseUrl}/dir/?api=1&origin=${member.address.latitude},
          ${member.address.longitude}&destination=${dataItem.practiceAddress.latitude},${dataItem.practiceAddress.longitude}`;
          window.open(url, '_blank');
        }
      }
    });
  }

  /**
   * Execute search on enter
   *
   * @param e Event
   */
  public keyDown(event: KeyboardEvent): void
  {
    if (event && event.key === 'Enter' && !this.searchDisabled())
    {
      event.preventDefault();
      this.searchProvider(true);
    }
  }

  /**
   * Filters the specialties based on user input.
   */
  public filterSpecialties(filter: string): void
  {
    this.searchTerm = filter;
    this.specialtiesFiltered = this.allSpecialties
      .filter(specialty => specialty.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  public onSelectedKeysChange(e: any): void
  {
    this.keys = e;
    this.onSelectedProviders();
  }

  public openUrl(url: string): void
  {
    window.open(url);
  }

  public showOrganization(dataItem: any): boolean
  {
    return dataItem.fullName.replace(/ /g, '') !== '';
  }

  public filterLanguages(filter: string): void
  {
    this.searchTerm = filter;
    this.languagesFiltered = sortBy(this.allLanguages
      .filter(item => item.displayName.toLowerCase().includes(filter.toLowerCase())), ['displayName']);
  }

  public updateSplitterBar()
  {
    // do not run if no splitter is loaded
    if (!this.splitter?.nativeElement) { return; }

    const bar = this.splitter.nativeElement.querySelector('.k-splitbar');

    if (bar)
    {
      const pane = this.splitter.nativeElement.querySelector('.k-pane');
      const size = pane ? pane.offsetHeight : 0;

      bar.setAttribute('aria-valuenow', size.toString());
      bar.setAttribute('aria-valuemax', '9999');
    }
  }

  /**
   * Set the Provider search filters.
   */
  private setSearchFilters(): void
  {
    if (this.firstName.value)
    {
      this.gridState.filter.filters.push({ field: 'firstName', operator: 'startswith', value: this.firstName.value });
    }

    if (this.lastName.value)
    {
      this.gridState.filter.filters.push({ field: 'lastName', operator: 'startswith', value: this.lastName.value });
    }
    else
    {
      this.gridState.filter.filters.push({ field: 'lastName', operator: 'isnotnull', value: '' });
    }

    if (this.middleName.value)
    {
      this.gridState.filter.filters.push({ field: 'middleName', operator: 'startswith', value: this.middleName.value });
    }

    if (this.organization.value)
    {
      this.gridState.filter.filters.push({ field: 'organization', operator: 'contains', value: this.organization.value });
    }

    if (this.facility.value)
    {
      this.gridState.filter.filters.push({ field: 'facilityName', operator: 'contains', value: this.facility.value });
    }

    if (this.gender.value)
    {
      this.gridState.filter.filters.push({ field: 'gender', operator: 'eq', value: this.gender.value.code });
    }

    if (this.npi.value)
    {
      this.gridState.filter.filters.push({ field: 'npi', operator: 'startswith', value: this.npi.value });
    }

    if (this.specialtyName.value)
    {
      this.gridState.filter.filters.push({ field: 'specialtyName', operator: 'startswith', value: this.specialtyName.value });
    }

    if (this.streetLine.value)
    {
      this.gridState.filter.filters.push({ field: 'streetLine', operator: 'contains', value: this.streetLine.value });
      if (this.allowFacilitySearch)
      {
        this.gridState.filter.filters.push({ field: 'facilityStreetLine', operator: 'contains', value: this.streetLine.value });
      }
    }

    if (this.state.value)
    {
      this.gridState.filter.filters.push({ field: 'state', operator: 'eq', value: this.state.value.code });
      if (this.allowFacilitySearch)
      {
        this.gridState.filter.filters.push({ field: 'facilityState', operator: 'eq', value: this.state.value.code });
      }
    }

    if (this.city.value)
    {
      this.gridState.filter.filters.push({ field: 'city', operator: 'startswith', value: this.city.value });
      if (this.allowFacilitySearch)
      {
        this.gridState.filter.filters.push({ field: 'facilityCity', operator: 'startswith', value: this.city.value });
      }
    }

    if (this.postalCode.value)
    {
      this.gridState.filter.filters.push({ field: 'postalCode', operator: 'startswith', value: this.postalCode.value });
      if (this.allowFacilitySearch)
      {
        this.gridState.filter.filters.push({ field: 'facilityPostalCode', operator: 'startswith', value: this.postalCode.value });
      }
    }

    const languages = this.languages.value ? this.languages.value.map((x: Dictionary) => x.id) : [];
    if (languages.length > 0)
    {
      this.gridState.filter.filters.push({ field: 'languageIds', operator: 'eq', value: languages.join() });
    }
  }

  /**
   * Clear grid results
   */
  private clearGrid(): void
  {
    this.gridView = { data: [], total: 0 };
  }

  private buildForm(): void
  {
    // Set empty search results
    this.clearGrid();

    // Get the list required
    this.helperService.getTeamProviderDataFromMultipleSources().subscribe({
      next: (responseList) =>
      {
        this.genders = responseList[0];
        this.states = responseList[1];
        this.specialties = responseList[2];

        // Set search form
        this.searchForm = this.fb.group({
          npi: [null],
          specialtyName: [null],
          languages: [null],
          firstName: [null],
          lastName: [null],
          middleName: [null],
          gender: [null],
          streetLine: [null],
          city: [null],
          state: [null],
          postalCode: [null],
          organization: [null],
          facility: [null]
        });
        this.loaded = true;
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
  }

  /**
   * Reset search data and count.
   */
  private resetSearch(): void
  {
    this.keys = [];
    this.total = 0;
    this.selectProvider.emit([]);
  }

  /**
   * Load languages lookup
   */
  private loadLanguages(): void
  {
    this.dictionaryService.getCachedLanguages().subscribe({
      next: (data) =>
      {
        this.allLanguages = data;
        this.filterLanguages('');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }
}
